import { Card, Checkbox, Collapse, Form, Input, Space, Tag } from "antd"
import { PixelsEditorV2, UploadSoundComponent } from "components"
import Styles from "./styles.module.css"

import { UploadComponentAntdCompatibilityWrapper } from "components/upload/UploadImageAntdCompatibilityWrapper"

const { Panel } = Collapse

type Props = {
  parentIndex: number;
  bannerType: "endgameBanner" | "launchGameBanner";
  title: string;
}

export const SpecialEventGameScreenBannerEditor = ({ parentIndex, bannerType, title }: Props) => {
  const form = Form.useFormInstance()
  const banner = Form.useWatch(["notificationsPerGame", parentIndex, bannerType], form) || {}
  const trackingPixels = banner.trackingPixels || []
  const defaultOpenPanels = [
    banner.cta ? "cta" : "",
    trackingPixels.length ? "pixels" : "",
    banner.playSound ? "sound" : "",
  ].filter(Boolean)

  return (
    <Card
      size="small"
      className={Styles.wrapper}
      title={title}
      extra={(
        <Space size={4}>
          <Tag color={banner.image ? "success" : "error"}>{banner.image ? "Image ready" : "Missing image"}</Tag>
          <Tag color={trackingPixels.length ? "success" : "error"}>{trackingPixels.length} pixels</Tag>
        </Space>
      )}
    >
      <Form.Item
        name={[parentIndex, bannerType, "image"]}
        label="Banner image"
        tooltip={`The image URL to be shown on ${title.toLowerCase()}`}
        className={Styles.imageField}
      >
        <UploadComponentAntdCompatibilityWrapper />
      </Form.Item>

      <Collapse ghost defaultActiveKey={defaultOpenPanels} className={Styles.detailsCollapse}>
        <Panel header="CTA link" key="cta">
          <Form.Item
            name={[parentIndex, bannerType, "cta"]}
            tooltip="Optional link for the banner (call-to-action)"
          >
            <Input placeholder="Optional click URL" />
          </Form.Item>
        </Panel>

        <Panel header={`Tracking pixels (${trackingPixels.length})`} key="pixels">
          <Form.Item noStyle shouldUpdate>
            {() => (
              <Form.Item
                name={[parentIndex, bannerType, "trackingPixels"]}
                tooltip="Tracking pixels to be fired when the banner is shown"
              >
                <PixelsEditorV2
                  pixels={form.getFieldValue([
                    "notificationsPerGame",
                    parentIndex,
                    bannerType,
                    "trackingPixels",
                  ]) || []}
                  onChange={newPixels => {
                    form.setFieldValue(
                      ["notificationsPerGame", parentIndex, bannerType, "trackingPixels"],
                      newPixels,
                    )
                  }}
                />
              </Form.Item>
            )}
          </Form.Item>
        </Panel>

        <Panel header="Sound" key="sound">
          <Form.Item
            name={[parentIndex, bannerType, "playSound"]}
            valuePropName="checked"
            tooltip="Enable sound playback for this banner"
          >
            <Checkbox>Play Sound</Checkbox>
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => {
              const prevPlaySound = prevValues?.notificationsPerGame?.[parentIndex]?.[bannerType]?.playSound
              const currentPlaySound = currentValues?.notificationsPerGame?.[parentIndex]?.[bannerType]?.playSound

              return prevPlaySound !== currentPlaySound
            }}
          >
            {({ getFieldValue }) => {
              const playSound = getFieldValue(["notificationsPerGame", parentIndex, bannerType, "playSound"])

              return playSound ? (
                <Form.Item
                  name={[parentIndex, bannerType, "soundFile"]}
                  label="Sound File"
                  tooltip="Upload a sound file to play when this banner is shown"
                >
                  <UploadSoundComponent
                    soundFile={form.getFieldValue(["notificationsPerGame", parentIndex, bannerType, "soundFile"]) || ""}
                    setSoundFile={soundFile => {
                      const currentBanner = form.getFieldValue(["notificationsPerGame", parentIndex, bannerType]) || {}
                      form.setFieldsValue({
                        notificationsPerGame: {
                          [parentIndex]: {
                            [bannerType]: {
                              ...currentBanner,
                              soundFile,
                            },
                          },
                        },
                      })
                    }}
                    placeholder="Upload"
                  />
                </Form.Item>
              ) : null
            }}
          </Form.Item>
        </Panel>
      </Collapse>
    </Card>
  )
}
